import { useEffect, useMemo, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { ChevronDown, ChevronUp, Loader2, Save, ShieldAlert, Tag, X } from "lucide-react";
import { toast } from "sonner";

import { ApiError } from "~/lib/api";
import { useCurrentUser } from "~/lib/auth";
import {
    KILLCHAIN_PHASES,
    type IoCSighting,
    useIoC,
    useIoCSightings,
    useRevertIoC,
    useUpdateIoC,
} from "~/lib/iocs";
import {
    TypeBadge,
    severityLabel,
    severityPillClass,
} from "~/lib/iocVisuals";

import { CopyButton } from "./CopyButton";
import { SourceRollupTable } from "./SourceRollupTable";


interface IoCDetailDialogProps {
    iocId: string | null;
    onClose: () => void;
}

export function IoCDetailDialog({ iocId, onClose }: IoCDetailDialogProps): React.ReactElement | null {
    if (!iocId) return null;
    return <Inner iocId={iocId} onClose={onClose} />;
}

function Inner({ iocId, onClose }: { iocId: string; onClose: () => void }): React.ReactElement {
    const me = useCurrentUser();
    const canEdit = me.data?.roles.includes("admin") || me.data?.roles.includes("operator");

    const detail = useIoC(iocId);
    const sightings = useIoCSightings(iocId);
    const update = useUpdateIoC();
    const revert = useRevertIoC();

    const ioc = detail.data?.ioc;
    const sources = detail.data?.sources ?? [];

    // Brand-override diff vs. source-captured snapshot. Undefined source
    // keys mean we have no baseline yet — we hide the chip in that case.
    const src = ioc?.sourceValues ?? {};
    const sevOverridden =
        ioc !== undefined && src.severity !== undefined && src.severity !== ioc.severity;
    const tagsSorted = (a: string[] = []) => [...a].sort().join("|");
    const tagsOverridden =
        ioc !== undefined &&
        src.tags !== undefined &&
        tagsSorted(src.tags) !== tagsSorted(ioc.tags);
    const phaseOverridden =
        ioc !== undefined &&
        src.killchain_phase !== undefined &&
        (src.killchain_phase ?? null) !== (ioc.killchainPhase ?? null);

    // Local edit state
    const [editingTags, setEditingTags] = useState<string[] | null>(null);
    const [editingSeverity, setEditingSeverity] = useState<number | null>(null);
    const [editingPhase, setEditingPhase] = useState<string>("");
    const [tagInput, setTagInput] = useState("");

    useEffect(() => {
        if (ioc) {
            setEditingTags(ioc.tags);
            setEditingSeverity(ioc.severity);
            setEditingPhase(ioc.killchainPhase ?? "");
        }
    }, [ioc?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const dirty = useMemo(() => {
        if (!ioc || editingTags === null || editingSeverity === null) return false;
        if (editingSeverity !== ioc.severity) return true;
        if ((ioc.killchainPhase ?? "") !== editingPhase) return true;
        const a = [...ioc.tags].sort().join("|");
        const b = [...editingTags].sort().join("|");
        return a !== b;
    }, [ioc, editingTags, editingSeverity, editingPhase]);

    function addTag(t: string): void {
        const v = t.trim();
        if (!v || (editingTags ?? []).includes(v)) return;
        setEditingTags([...(editingTags ?? []), v]);
        setTagInput("");
    }
    function removeTag(t: string): void {
        setEditingTags((editingTags ?? []).filter((x) => x !== t));
    }

    async function save(): Promise<void> {
        if (!ioc) return;
        try {
            await update.mutateAsync({
                id: ioc.id,
                severity: editingSeverity ?? undefined,
                tags: editingTags ?? undefined,
                killchainPhase: editingPhase, // empty string clears the override
            });
            toast.success("IoC updated");
        } catch (err) {
            toast.error(err instanceof ApiError ? err.message : "Update failed");
        }
    }

    async function doRevert(fields: string[]): Promise<void> {
        if (!ioc) return;
        try {
            const res = await revert.mutateAsync({ id: ioc.id, fields });
            // Re-sync local edit buffers so the pending banner clears.
            setEditingSeverity(res.ioc.severity);
            setEditingTags(res.ioc.tags);
            setEditingPhase(res.ioc.killchainPhase ?? "");
            toast.success(`Reverted ${fields.join(", ")} to source`);
        } catch (err) {
            toast.error(err instanceof ApiError ? err.message : "Revert failed");
        }
    }

    return (
        <Dialog.Root open={true} onOpenChange={(o) => { if (!o) onClose(); }}>
            <Dialog.Portal>
                <Dialog.Overlay className="dialog-overlay">
                    <Dialog.Content className="dialog-content dialog-content--lg enter-up">
                    {/* Hero */}
                    <div
                        style={{
                            padding: "var(--space-6)",
                            borderBottom: "var(--border-hairline) solid var(--color-border)",
                        }}
                    >
                        <div className="cluster-xs" style={{ justifyContent: "space-between" }}>
                            {ioc ? <TypeBadge type={ioc.type} size="md" /> : <span className="type-caption">Loading…</span>}
                            <Dialog.Close asChild>
                                <button type="button" className="btn btn--ghost btn--sm" aria-label="Close">
                                    <X size={14} />
                                </button>
                            </Dialog.Close>
                        </div>
                        {ioc && (
                            <div
                                className="surface"
                                style={{
                                    marginTop: "var(--space-3)",
                                    padding: "var(--space-3)",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "var(--space-3)",
                                }}
                            >
                                <span
                                    className="type-mono"
                                    style={{
                                        flex: 1,
                                        wordBreak: "break-all",
                                        fontSize: "var(--font-size-13)",
                                    }}
                                >
                                    {ioc.value}
                                </span>
                                <CopyButton value={ioc.value} />
                            </div>
                        )}
                    </div>

                    {detail.isLoading && (
                        <div className="empty-state" style={{ padding: "var(--space-12)" }}>
                            <Loader2 size={20} className="empty-state__icon animate-spin" aria-hidden />
                            <div className="empty-state__body">Loading details…</div>
                        </div>
                    )}

                    {ioc && editingTags !== null && editingSeverity !== null && (
                        <div className="stack-lg" style={{ padding: "var(--space-6)" }}>
                            {/* Quick stats */}
                            <section
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                                    gap: "var(--space-3)",
                                }}
                            >
                                <Stat label="Severity">
                                    <span className={`status-pill ${severityPillClass(ioc.severity)}`}>
                                        {ioc.severity} · {severityLabel(ioc.severity)}
                                    </span>
                                </Stat>
                                <Stat label="Sources">
                                    <span className="tabular type-h2">{ioc.sourceCount}</span>
                                </Stat>
                                <Stat label="First seen">
                                    <span className="text-12 text-text-muted">
                                        {new Date(ioc.firstSeen).toLocaleString()}
                                    </span>
                                </Stat>
                                <Stat label="Last seen">
                                    <span className="text-12 text-text-muted">
                                        {new Date(ioc.lastSeen).toLocaleString()}
                                    </span>
                                </Stat>
                            </section>

                            {/* Severity editor */}
                            <section className="card stack-sm">
                                <div className="cluster-xs" style={{ justifyContent: "space-between" }}>
                                    <h3 className="type-h3">Severity</h3>
                                    <span className="text-12 text-text-muted">1–10</span>
                                </div>
                                {sevOverridden && (
                                    <div className="cluster-xs" style={{ gap: "var(--space-2)", flexWrap: "wrap" }}>
                                        <span className="chip chip--info" style={{ height: 20 }}>
                                            overridden · source: <code>{String(src.severity)}</code>
                                        </span>
                                        {canEdit && (
                                            <button
                                                type="button"
                                                className="btn btn--ghost btn--sm"
                                                onClick={() => doRevert(["severity"])}
                                                disabled={revert.isPending}
                                            >
                                                Revert
                                            </button>
                                        )}
                                    </div>
                                )}
                                <SeverityScale
                                    value={editingSeverity}
                                    disabled={!canEdit}
                                    onChange={setEditingSeverity}
                                />
                            </section>

                            {/* Kill-chain phase editor */}
                            <section className="card stack-sm">
                                <div className="cluster-xs" style={{ justifyContent: "space-between" }}>
                                    <h3 className="type-h3">Kill-chain phase</h3>
                                    <span className="text-12 text-text-muted">
                                        {ioc.killchainPhase
                                            ? "operator-set or heuristic"
                                            : "unclassified"}
                                    </span>
                                </div>
                                {phaseOverridden && (
                                    <div className="cluster-xs" style={{ gap: "var(--space-2)", flexWrap: "wrap" }}>
                                        <span className="chip chip--info" style={{ height: 20 }}>
                                            overridden · source:{" "}
                                            <code>{src.killchain_phase ?? "—"}</code>
                                        </span>
                                        {canEdit && (
                                            <button
                                                type="button"
                                                className="btn btn--ghost btn--sm"
                                                onClick={() => doRevert(["killchain_phase"])}
                                                disabled={revert.isPending}
                                            >
                                                Revert
                                            </button>
                                        )}
                                    </div>
                                )}
                                <p className="text-12 text-text-muted">
                                    Lockheed Martin Cyber Kill Chain stage. The ingest pipeline assigns
                                    one heuristically; an operator can override or clear it here.
                                </p>
                                <select
                                    className="input"
                                    value={editingPhase}
                                    disabled={!canEdit}
                                    onChange={(e) => setEditingPhase(e.target.value)}
                                >
                                    <option value="">— unclassified —</option>
                                    {KILLCHAIN_PHASES.map((p) => (
                                        <option key={p.slug} value={p.slug}>
                                            {p.label}
                                        </option>
                                    ))}
                                </select>
                            </section>

                            {/* Tags editor */}
                            <section className="card stack-sm">
                                <div className="cluster-xs" style={{ justifyContent: "space-between" }}>
                                    <h3 className="type-h3">
                                        <Tag size={14} className="text-accent" style={{ display: "inline", marginRight: 6 }} />
                                        Tags
                                    </h3>
                                    <span className="text-12 text-text-muted">{editingTags.length}</span>
                                </div>
                                {tagsOverridden && (
                                    <div className="cluster-xs" style={{ gap: "var(--space-2)", flexWrap: "wrap" }}>
                                        <span className="chip chip--info" style={{ height: 20 }}>
                                            overridden · source:{" "}
                                            <code>{(src.tags ?? []).join(", ") || "∅"}</code>
                                        </span>
                                        {canEdit && (
                                            <button
                                                type="button"
                                                className="btn btn--ghost btn--sm"
                                                onClick={() => doRevert(["tags"])}
                                                disabled={revert.isPending}
                                            >
                                                Revert
                                            </button>
                                        )}
                                    </div>
                                )}
                                {tagsOverridden && (src.tags ?? []).filter((t) => !ioc.tags.includes(t)).length > 0 && (
                                    <div className="tag-stack">
                                        {(src.tags ?? [])
                                            .filter((t) => !ioc.tags.includes(t))
                                            .map((t) => (
                                                <span
                                                    key={"src-" + t}
                                                    className="chip"
                                                    title="Source-supplied tag removed by an operator"
                                                    style={{
                                                        textDecoration: "line-through",
                                                        opacity: 0.55,
                                                    }}
                                                >
                                                    {t}
                                                </span>
                                            ))}
                                    </div>
                                )}
                                <div className="tag-stack">
                                    {editingTags.map((t) => {
                                        const isOperatorAdded =
                                            src.tags !== undefined && !(src.tags ?? []).includes(t);
                                        return (
                                        <span
                                            key={t}
                                            className="chip"
                                            style={{ paddingRight: 4 }}
                                            title={isOperatorAdded ? "Operator-added (not in source)" : undefined}
                                        >
                                            {isOperatorAdded && (
                                                <span
                                                    aria-hidden
                                                    style={{
                                                        marginRight: 3,
                                                        color: "var(--color-accent)",
                                                        fontWeight: 600,
                                                    }}
                                                >
                                                    +
                                                </span>
                                            )}
                                            {t}
                                            {canEdit && (
                                                <button
                                                    type="button"
                                                    onClick={() => removeTag(t)}
                                                    aria-label={`Remove ${t}`}
                                                    style={{
                                                        marginLeft: 4,
                                                        border: 0,
                                                        background: "transparent",
                                                        cursor: "pointer",
                                                        color: "currentColor",
                                                        opacity: 0.7,
                                                        padding: 0,
                                                        display: "inline-flex",
                                                    }}
                                                >
                                                    <X size={11} />
                                                </button>
                                            )}
                                        </span>
                                        );
                                    })}
                                    {editingTags.length === 0 && (
                                        <span className="text-text-subtle text-12">No tags yet.</span>
                                    )}
                                </div>
                                {canEdit && (
                                    <div className="cluster-xs">
                                        <input
                                            className="input"
                                            placeholder="Add tag…"
                                            value={tagInput}
                                            onChange={(e) => setTagInput(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") {
                                                    e.preventDefault();
                                                    addTag(tagInput);
                                                }
                                            }}
                                            style={{ flex: 1 }}
                                        />
                                        <button
                                            type="button"
                                            className="btn btn--secondary btn--sm press-crush"
                                            onClick={() => addTag(tagInput)}
                                            disabled={!tagInput.trim()}
                                        >
                                            Add
                                        </button>
                                    </div>
                                )}
                            </section>

                            {/* Sources roll-up — TanStack Table v8 view of
                               the verdict envelope's provenance.sources[].
                               Falls back to the legacy chip row when the
                               envelope hasn't loaded yet. */}
                            <SourceRollupTable iocId={ioc.id} fallbackSources={sources} />

                            {/* Sightings (collapsible per row to keep dialog tight) */}
                            <section className="stack-sm">
                                <h3 className="type-h3">Recent sightings</h3>
                                {sightings.isLoading && (
                                    <div className="text-12 text-text-muted cluster-xs">
                                        <Loader2 size={14} className="animate-spin" aria-hidden /> loading…
                                    </div>
                                )}
                                {sightings.data && sightings.data.length === 0 && (
                                    <span className="text-text-subtle text-12">No sightings yet.</span>
                                )}
                                <div className="stack-xs">
                                    {(sightings.data ?? []).map((s) => (
                                        <SightingRow key={s.id} sighting={s} />
                                    ))}
                                </div>
                            </section>
                        </div>
                    )}

                    {/* Sticky action bar */}
                    {ioc && canEdit && (
                        <div
                            style={{
                                position: "sticky",
                                bottom: 0,
                                background: "var(--color-surface-raised)",
                                borderTop: "var(--border-hairline) solid var(--color-border)",
                                padding: "var(--space-3) var(--space-6)",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                gap: "var(--space-3)",
                            }}
                        >
                            <span className="text-12 text-text-muted">
                                {dirty ? "Unsaved changes" : "Up to date"}
                            </span>
                            <div className="cluster-xs">
                                <button
                                    type="button"
                                    className="btn btn--ghost"
                                    onClick={() => {
                                        setEditingTags(ioc.tags);
                                        setEditingSeverity(ioc.severity);
                                        setEditingPhase(ioc.killchainPhase ?? "");
                                    }}
                                    disabled={!dirty}
                                >
                                    Reset
                                </button>
                                <button
                                    type="button"
                                    className="btn btn--primary press-crush"
                                    onClick={save}
                                    disabled={!dirty || update.isPending}
                                >
                                    {update.isPending ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                                    <span>{update.isPending ? "Saving…" : "Save"}</span>
                                </button>
                            </div>
                        </div>
                    )}
                    </Dialog.Content>
                </Dialog.Overlay>
            </Dialog.Portal>
        </Dialog.Root>
    );
}

function Stat({ label, children }: { label: string; children: React.ReactNode }): React.ReactElement {
    return (
        <div
            className="surface"
            style={{
                padding: "var(--space-3)",
                display: "flex",
                flexDirection: "column",
                gap: 4,
            }}
        >
            <span className="type-caption">{label}</span>
            <div>{children}</div>
        </div>
    );
}

interface SeverityScaleProps {
    value: number;
    disabled?: boolean;
    onChange: (v: number) => void;
}

function SeverityScale({ value, disabled, onChange }: SeverityScaleProps): React.ReactElement {
    return (
        <div className="cluster-xs" style={{ gap: 4 }}>
            {Array.from({ length: 10 }, (_, i) => {
                const n = i + 1;
                const active = n <= value;
                const cls = severityPillClass(n);
                return (
                    <button
                        key={n}
                        type="button"
                        disabled={disabled}
                        aria-label={`Set severity to ${n}`}
                        onClick={() => onChange(n)}
                        className={active ? cls : ""}
                        style={{
                            width: 32,
                            height: 28,
                            border: active ? "0" : "var(--border-hairline) solid var(--color-border)",
                            borderRadius: "var(--radius-sm)",
                            cursor: disabled ? "not-allowed" : "pointer",
                            color: active ? undefined : "var(--color-text-muted)",
                            background: active ? undefined : "transparent",
                            fontFamily: "var(--font-family-mono)",
                            fontSize: "var(--font-size-12)",
                            transition: "all 80ms var(--ease-standard)",
                        }}
                    >
                        {n}
                    </button>
                );
            })}
        </div>
    );
}

function SightingRow({ sighting }: { sighting: IoCSighting }): React.ReactElement {
    const [open, setOpen] = useState(false);
    return (
        <div className="surface" style={{ padding: 0 }}>
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                style={{
                    width: "100%",
                    background: "transparent",
                    border: 0,
                    textAlign: "left",
                    padding: "var(--space-2) var(--space-3)",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "var(--space-3)",
                    color: "inherit",
                }}
            >
                <ShieldAlert size={14} className="text-accent" aria-hidden />
                <span className="chip chip--accent">{sighting.sourceSlug}</span>
                <span className="text-12 text-text-muted">
                    {new Date(sighting.seenAt).toLocaleString()}
                </span>
                <span style={{ marginLeft: "auto" }}>
                    {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </span>
            </button>
            {open && (
                <pre
                    style={{
                        margin: 0,
                        padding: "var(--space-3)",
                        borderTop: "var(--border-hairline) solid var(--color-border)",
                        fontSize: "var(--font-size-11)",
                        fontFamily: "var(--font-family-mono)",
                        color: "var(--color-text-muted)",
                        overflow: "auto",
                        maxHeight: 240,
                    }}
                >
                    {JSON.stringify(sighting.raw, null, 2)}
                </pre>
            )}
        </div>
    );
}
